<template>
  <div class="container">
    <el-scrollbar>
      <!-- ===== 追加済みアイテム ===== -->
      <div
        v-for="item in addedItems"
        :key="item._id"
        class="item"
        :class="{ selected: isSelected(item) }"
        @click="handleSelect(item)"
        @dblclick="handleEdit(item)"
      >
          <el-input
              :ref="`input-${item._id}`"
              v-model="item.label"
              :readonly="!(editingItem && editingItem._id === item._id)"
              :class="[
                'list-input',
                isEditing(item) ? 'editable' : 'readonly',
                isDuplicate(item) ? 'duplicate' : '',
                isEmpty(item.label) ? 'empty' : '',
            　]"
              placeholder="入力してください"
              @input="onItemInput(item)"
              @blur="handleBlur($event, item)"
              @compositionstart.native="onCompositionStart"
              @compositionend.native="onCompositionEnd($event, item)"
          />

        <div class="wrapperIcon">
          <img
            src="@/assets/images/delete.svg"
            alt="削除"
            class="icon"
            @click.stop="handleDelete(item)"
          />
        </div>
      </div>

      <!-- ===== 追加前アイテム ===== -->
      <div
        v-if="canAddMore"
        class="pendingItem"
        :class="{ selected: isSelected(pendingItem) }"
        @click="handleSelect(pendingItem)"
      >
        <el-input
          ref="refPendingInput"
          class="pending-input"
          v-model="pendingItemLabel"
          :disabled="
            (!isPendingFocused && invalidLabels) ||
            (!isPendingFocused && disableNewSelection && (!selectedItem || selectedItem._id !== pendingItem._id))
          "
          :class="[isPendingFocused ? 'editable' : '', isDuplicate(pendingItem) ? 'duplicate' : '']"
          placeholder="入力してください"
          @input="onPendingInput"
          @focus="isPendingFocused = true"
          @blur="handleBlur($event, pendingItem)"
          @compositionstart.native="onCompositionStart"
          @compositionend.native="onCompositionEnd($event, 'pending')"
        />
        <div class="wrapperIcon" @click.stop>
          <BaseButton
            type="primary"
            label="追加"
            :disable="isEmpty(pendingItemLabel.trim()) || isDuplicate(pendingItem) || disableAdd"
            @click="handleAdd"
          />
        </div>
      </div>
    </el-scrollbar>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import BaseButton from '@/components/pc/radio/ipradio/atoms/BaseButton.vue'

export default {
  name: 'BCListBoxVue2',
  components: { BaseButton },

  props: {
    items: { type: Array, default: () => [] },
    disableAdd: { type: Boolean, default: false },
    disableNewSelection: { type: Boolean, default: false },
    maxItems: { type: Number, default: 20 },
    labelMaxBytes: { type: Number, default: 32 },
  },

  data () {
    return {
      // ローカルコピー（編集用）
      addedItems: this.items.slice(),
      // 追加前
      pendingItemLabel: '',
      pendingItemId: uuidv4(),
      // 状態
      selectedItem: null,
      editingItem: null,
      isPendingFocused: false,
      invalidLabels: false,
      startComposition: false,
    }
  },

  computed: {
    pendingItem () {
      return { label: this.pendingItemLabel, value: this.pendingItemLabel, _id: this.pendingItemId }
    },
    canAddMore () {
      return this.addedItems.length < this.maxItems
    },
  },

  watch: {
    // 親 props.items の反映（追加/削除検知＆選択状態の調整）
    items: {
      immediate: true,
      deep: true,
      handler (val, oldVal) {
        const before = this.addedItems.length
        this.addedItems = (val || []).slice()

        this.$nextTick(() => {
          if (before === this.addedItems.length) return

          if (before < this.addedItems.length) {
            // 追加時は最後を選択
            const focusItem = this.addedItems.length ? this.addedItems[this.addedItems.length - 1] : null
            this.handleSelect(focusItem)
          } else {
            // 削除時に選択中が消えてたら選択解除
            if (this.selectedItem) {
              const exists = this.addedItems.find(i => i._id === this.selectedItem._id)
              if (!exists) this.handleSelect(null)
            }
          }
        })
      }
    },

    // ラベル検証
    addedItems: {
      deep: true,
      handler () { this.checkLabels() }
    },
    pendingItemLabel () {
      this.checkLabels()
      this.$emit('typing', { ...this.pendingItem, label: this.pendingItemLabel })
    },
  },

  methods: {
    // 文字列ユーティリティ
    isEmpty (v) { return v == null || String(v).trim() === '' },
    isDuplicate (item) {
      const id = item && item._id
      const label = item && item.label
      return this.addedItems.some(_ => _._id !== id && _.label !== '' && _.label === label)
    },
    isSelected (item) { return this.selectedItem && item && this.selectedItem._id === item._id },
    isEditing (item) { return this.editingItem && item && this.editingItem._id === item._id },

    // 選択
    async handleSelect (item) {
      if (item == null) {
        this.selectedItem = null
        this.$emit('select', null)
        return
      }
      if (this.disableNewSelection || this.invalidLabels) return

      // 追加行のフォーカス制御
      if (this.canAddMore) {
        if (this.pendingItem._id !== item._id) {
          this.isPendingFocused = false
          if (this.$refs.refPendingInput) this.$refs.refPendingInput.blur()
        } else {
          this.isPendingFocused = true
          this.$nextTick(() => this.$refs.refPendingInput && this.$refs.refPendingInput.focus())
        }
      }

      if (!this.selectedItem || this.selectedItem._id !== item._id) {
        this.selectedItem = item
        this.$emit('select', this.selectedItem)
      }
    },

    // ブラー時
    handleBlur (evt, item) {
      if (!this.selectedItem || !item || this.selectedItem._id !== item._id) return

      this.checkLabels()
      if (this.invalidLabels) { evt.target && evt.target.focus(); return }

      const isLabelEmpty = this.isEmpty(item.label)
      if (isLabelEmpty && this.pendingItem._id !== item._id) {
        evt.target && evt.target.focus()
        return
      }

      this.isPendingFocused = false
      this.$emit('confirmed', item)
      this.editingItem = null
    },

    // 追加
    handleAdd () {
      const label = (this.pendingItemLabel || '').trim()
      if (!label || this.isDuplicate({ _id: this.pendingItem._id, label })) return
      this.$emit('add', this.pendingItem)
      this.pendingItemLabel = ''
      this.pendingItemId = uuidv4()
    },

    // 編集
      async handleEdit (item) {
          if (!this.selectedItem || this.selectedItem._id !== item._id) return
          this.editingItem = item
          await this.$nextTick()

          const ref = this.$refs[`input-${item._id}`]
          const input = Array.isArray(ref) ? ref[0] : ref
          if (input && typeof input.focus === 'function') {
              input.focus()
          }
      },

    // 削除
    handleDelete (item) {
      this.$emit('delete', item)
    },

    // ラベル検証（空/重複）
    checkLabels () {
      const labels = this.addedItems.map(i => i.label)
      if ((this.pendingItemLabel || '').trim()) labels.push(this.pendingItemLabel.trim())

      const hasEmpty = labels.some(l => this.isEmpty(l))
      const hasDup = labels.some((l, i) => labels.indexOf(l) !== i)

      this.invalidLabels = hasEmpty || hasDup
      this.$emit('invalidLabels', this.invalidLabels)
    },

    // IME
    onCompositionStart () { this.startComposition = true },
    onCompositionEnd (e, target) {
      this.startComposition = false
      if (target === 'pending') {
        this.pendingItemLabel = this.trimToUtf8ByteLimit(this.pendingItemLabel)
      } else if (target && typeof target === 'object') {
        target.label = this.trimToUtf8ByteLimit(target.label)
      }
    },

    // 入力時 emit（Vue2 では個別にフックするのが確実）
    onItemInput (item) {
      item.label = this.trimToUtf8ByteLimit(item.label || '')
      this.$emit('typing', { ...item, label: item.label })
    },
    onPendingInput () {
      this.pendingItemLabel = this.trimToUtf8ByteLimit(this.pendingItemLabel || '')
      this.$emit('typing', { ...this.pendingItem, label: this.pendingItemLabel })
    },

    // バイト長制限（UTF-8）
    trimToUtf8ByteLimit (str, maxBytes) {
      const limit = maxBytes || this.labelMaxBytes
      const encoder = new TextEncoder()
      let res = ''
      for (let i = 0; i < String(str).length; i++) {
        const t = res + str[i]
        if (encoder.encode(t).length > limit) break
        res = t
      }
      return res
    },
  },
}
</script>

<style scoped>
.container {
  height: 200px;
  padding: 8px;
  overflow-y: auto;
  border: 1px solid #ccc;
}

.item,
.pendingItem {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-around;
  margin: 0 2rem 16px 2rem;
  transition: background-color 0.2s;
}

.item:hover,
.item.selected,
.pendingItem:hover,
.pendingItem.selected { background-color: #ecf1fa; }

.list-input,
.pending-input {
    width: 80%;
}

.list-input >>> .el-input__inner,
.pending-input >>> .el-input__inner {
    background-color: #f9f9f9;
    border: 1px solid #ccc;
    transition: border-color 0.2s;
}

.list-input >>> .el-input__inner:focus,
.pending-input >>> .el-input__inner:focus {
    outline: none;
}

.list-input.editable >>> .el-input__inner,
.pending-input.editable >>> .el-input__inner {
    cursor: text;
    background-color: #fff;
    border-color: #66afe9;
}

.list-input.readonly >>> .el-input__inner {
    cursor: default;
}

.list-input.duplicate >>> .el-input__inner,
.list-input.empty >>> .el-input__inner,
.pending-input.duplicate >>> .el-input__inner {
    border-color: red;
}

.pending-input >>> .el-input__inner {
    background-color: #fff;
    border: 1px solid #ccc;
}

.wrapperIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 32px;
  padding: 0;
  cursor: pointer;
  background: none;
  border: none;
}
.icon { width: 16px; height: 16px; transition: filter 0.2s; }
.wrapperIcon:hover .icon { filter: drop-shadow(0 0 2px rgba(0,0,0,.35)); }
</style>
